import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axiosClient } from "@/lib/axiosClient";

export interface Participant {
    participant_id: number;
    pic_id?: number | null;
    full_name: string;
    gender?: string | null;
    blood_type?: string | null;
    clothing_size?: string | null;
    birth_date?: string | null;
    birth_place?: string | null;
    phone_mobile?: string | null;
    phone_number?: string | null;
    email?: string | null;
    nik?: string | null;
    religion_id?: number | null;
    country_id?: number | null;
    address?: string | null;
    institution?: string | null;
    institution_address?: string | null;
    position?: string | null;
    city?: string | null;
    postal_code?: string | null;
    slug?: string | null;
    photo_path?: string | null;
    registration_type?: string | null;
    is_active: boolean;
    is_valid: boolean;
    created_at?: string;
    updated_at?: string;
    linked_user?: { user_id: number; name: string; email: string } | null;
}

export type ParticipantPayload = Omit<Participant, "participant_id" | "created_at" | "updated_at">;

export interface ParticipantFilters {
    institution?: string;
    gender?: string;
    blood_type?: string;
    clothing_size?: string;
    registration_type?: string;
    is_active?: string;
    is_valid?: string;
    religion_id?: number | string;
    country_id?: number | string;
    period_id?: number | string;
    payment_status?: string;
}

export interface ParticipantStats {
    total: number;
    valid: number;
    invalid: number;
    personal: number;
    kolektif: number;
    male: number;
    female: number;
}

export const useAdminParticipants = (page = 1, search = "", perPage = 10, filters: ParticipantFilters = {}) => {
    return useQuery({
        queryKey: ["admin-participants", page, search, perPage, filters],
        queryFn: async () => {
            const { data } = await axiosClient.get("/participants", {
                params: { page, search, per_page: perPage, ...filters },
                withCredentials: true
            });
            return data.data; // asumsi data pagination ada di data.data, sesuai backend
        },
    });
};

export const useAdminParticipantStats = (search = "", filters: ParticipantFilters = {}) => {
    return useQuery<ParticipantStats>({
        queryKey: ["admin-participants-stats", search, filters],
        queryFn: async () => {
            const { data } = await axiosClient.get("/participants/stats", {
                params: { search, ...filters },
                withCredentials: true
            });
            return data.data;
        },
    });
};

export const useParticipantInstitutions = () => {
    return useQuery<string[]>({
        queryKey: ["participant-institutions"],
        queryFn: async () => {
            const { data } = await axiosClient.get("/participants/institutions", { withCredentials: true });
            return data;
        },
        staleTime: 1000 * 60 * 60,
    });
};

export const useAdminParticipant = (id: number | null) => {
    return useQuery<{ success: boolean; data: Participant }>({
        queryKey: ["admin-participant", id],
        queryFn: async () => {
            const { data } = await axiosClient.get(`/participants/${id}`, { withCredentials: true });
            return data;
        },
        enabled: !!id,
    });
};

// CREATE Participant (Menggunakan FormData karena ada upload photo)
export const useCreateParticipant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (payload: FormData) => {
            const { data } = await axiosClient.post("/participants", payload, { 
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" }
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-participants"] });
            queryClient.invalidateQueries({ queryKey: ["admin-participants-stats"] });
        },
    });
};

// UPDATE Participant (Menggunakan FormData)
export const useUpdateParticipant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: number; payload: FormData }) => {
            const { data } = await axiosClient.post(`/participants/${id}`, payload, { 
                withCredentials: true,
                headers: { "Content-Type": "multipart/form-data" }
            });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-participants"] });
            queryClient.invalidateQueries({ queryKey: ["admin-participants-stats"] });
        },
    });
};

// DELETE Participant
export const useDeleteParticipant = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (id: number) => {
            const { data } = await axiosClient.delete(`/participants/${id}`, { withCredentials: true });
            return data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["admin-participants"] });
            queryClient.invalidateQueries({ queryKey: ["admin-participants-stats"] });
        },
    });
};
